import { useLoad } from "@redotech/react-util/load";
import { DEFAULT_CATALOG_VIEW } from "@redotech/redo-inventory-common/views/default-catalog-products-views";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { ProductFamilyId, ProductId } from "@redotech/redo-product-id";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { useContext, useMemo } from "react";

const MAX_PRODUCT_IDS_PER_REQUEST = 500;

export function getProductPageHref({
  inventoryManagementBaseUrl,
  productFamilyId,
  productId,
}: {
  inventoryManagementBaseUrl: string;
  productFamilyId: ProductFamilyId;
  productId: ProductId;
}): string {
  return `${inventoryManagementBaseUrl}/catalog/products/${DEFAULT_CATALOG_VIEW}/${productFamilyId}/${productId}`;
}

/**
 * Links for Redo products, keyed by product ID. Products that can't be resolved
 * (catalog disabled, no inventory access, deleted, or not yet loaded) are
 * omitted so callers render them unlinked.
 */
export function useProductPageHrefs(
  redoProductIds: readonly (ProductId | null | undefined)[],
): ReadonlyMap<string, string> {
  const inventoryRpcClient = useContext(RedoInventoryRpcClientContext);
  const routes = useContext(MerchantRoutesContext);
  const team = useContext(TeamContext);
  const catalogEnabled = team?.settings.ims?.catalogEnabled ?? false;
  const inventoryManagementBaseUrl = routes?.inventoryManagementBaseUrl;

  const productIds = useMemo(() => {
    const ids = new Set<ProductId>();
    for (const id of redoProductIds) {
      if (id) {
        ids.add(id);
      }
    }
    return [...ids].sort().slice(0, MAX_PRODUCT_IDS_PER_REQUEST);
  }, [redoProductIds]);
  const productIdsKey = productIds.join(",");

  const load = useLoad(
    async (signal) => {
      if (
        !catalogEnabled ||
        !inventoryRpcClient ||
        inventoryManagementBaseUrl == null ||
        productIds.length === 0
      ) {
        return new Map<string, string>();
      }
      const { items } = await inventoryRpcClient.getProductInventoryLevels(
        { productIds },
        { signal },
      );
      const hrefs = new Map<string, string>();
      for (const { productId, productFamilyId } of items) {
        hrefs.set(
          productId,
          getProductPageHref({
            inventoryManagementBaseUrl,
            productFamilyId,
            productId,
          }),
        );
      }
      return hrefs;
    },
    // productIdsKey stands in for productIds, which is a fresh array each render.
    [
      catalogEnabled,
      inventoryRpcClient,
      inventoryManagementBaseUrl,
      productIdsKey,
    ],
  );

  return load.value ?? EMPTY_HREFS;
}

const EMPTY_HREFS: ReadonlyMap<string, string> = new Map();
